import { LoaderCircle, Save } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import {
  apiClient,
  type AssignmentTarget,
  type ThemeAssignment
} from "@/shared/api/api-client";
import { errorMessage } from "@/shared/api/http";
import { useUnsavedChanges } from "@/shared/unsaved-changes/UnsavedChanges";
import { SearchSelect, type SearchSelectOption } from "@/shared/components/SearchSelect";

/**
 * "Publicação destes publicadores/veículos sempre recebe este tema."
 *
 * Mora no formulário do tema e SÓ em modo edição, pelo mesmo motivo dos
 * apelidos de canal: a regra é um registro que aponta pro tema, e tema
 * novo ainda não tem id onde pendurá-la.
 *
 * Tem botão próprio de salvar, separado do "Salvar alterações" do tema:
 * são duas escritas no servidor (o tema e a regra), e um botão só
 * deixaria a tela num estado em que uma gravou e a outra falhou sem
 * dizer qual. O servidor recebe as três listas inteiras e substitui.
 *
 * Grupos e publicadores vêm inteiros (são poucos) e filtram na caixa;
 * veículo busca no servidor, porque o cadastro chega a ~14 mil.
 */

type Kind = "publisher_groups" | "publisher_persons" | "vehicles";

type Lists = Record<Kind, AssignmentTarget[]>;

const EMPTY: Lists = { publisher_groups: [], publisher_persons: [], vehicles: [] };

const KIND_LABEL: Record<Kind, string> = {
  publisher_groups: "Grupo de publicador",
  publisher_persons: "Publicador",
  vehicles: "Veículo"
};

function sameIds(a: AssignmentTarget[], b: AssignmentTarget[]): boolean {
  if (a.length !== b.length) return false;
  const ids = new Set(a.map(item => item.id));
  return b.every(item => ids.has(item.id));
}

function listsOf(rule: ThemeAssignment): Lists {
  return {
    publisher_groups: rule.publisher_groups,
    publisher_persons: rule.publisher_persons,
    vehicles: rule.vehicles
  };
}

export function ThemeAssignmentPanel({
  token,
  themeId,
  competitorId = null,
  onSaved
}: {
  token: string;
  themeId: number;
  /** O concorrente dono do tema, quando o cadastro é dele. Os ALVOS
   *  (grupos, publicadores, veículos) são sempre os do cliente. */
  competitorId?: number | null;
  /** Depois de gravar — a aba atualiza o selo "regra" da lista. */
  onSaved: () => void;
}) {
  const [saved, setSaved] = useState<Lists | null>(null);
  const [draft, setDraft] = useState<Lists>(EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // As opções ATIVAS de grupo e de publicador. Carregadas aqui, e não
  // recebidas da aba: a aba de Temas não carrega os publicadores.
  const [publisherGroups, setPublisherGroups] = useState<AssignmentTarget[]>([]);
  const [publisherPersons, setPublisherPersons] = useState<AssignmentTarget[]>([]);
  // Os nomes dos veículos que a busca devolveu: a caixa entrega só o id
  // escolhido, e o chip precisa do nome.
  const vehicleNames = useRef(new Map<number, string>());

  useEffect(() => {
    let alive = true;
    setSaved(null);
    setDraft(EMPTY);
    setError(null);
    Promise.all([
      apiClient.onCompetitor(competitorId).getThemeAssignment(token, themeId),
      apiClient.listPublisherGroups(token),
      apiClient.listPublisherPersons(token)
    ])
      .then(([rule, groups, persons]) => {
        if (!alive) return;
        setPublisherGroups(groups);
        setPublisherPersons(persons);
        setSaved(listsOf(rule));
        setDraft(listsOf(rule));
      })
      .catch(reason => {
        if (alive) setError(errorMessage(reason, "Não foi possível carregar a regra do subtema."));
      });
    return () => {
      alive = false;
    };
  }, [token, themeId, competitorId]);

  const searchVehicles = useCallback(
    async (query: string): Promise<SearchSelectOption[]> => {
      const page = await apiClient.listVehicles(token, { q: query, size: 30 });
      for (const vehicle of page.items) vehicleNames.current.set(vehicle.id, vehicle.name);
      return page.items.map(vehicle => ({ value: String(vehicle.id), label: vehicle.name }));
    },
    [token]
  );

  const add = (kind: Kind, target: AssignmentTarget) =>
    setDraft(prev =>
      prev[kind].some(item => item.id === target.id)
        ? prev
        : { ...prev, [kind]: [...prev[kind], target] }
    );

  const remove = (kind: Kind, id: number) =>
    setDraft(prev => ({ ...prev, [kind]: prev[kind].filter(item => item.id !== id) }));

  const dirty =
    saved !== null &&
    (["publisher_groups", "publisher_persons", "vehicles"] as const).some(
      kind => !sameIds(saved[kind], draft[kind])
    );

  const save = async (): Promise<boolean> => {
    setSaving(true);
    setError(null);
    try {
      const rule = await apiClient
        .onCompetitor(competitorId)
        .replaceThemeAssignment(token, themeId, {
        publisher_group_ids: draft.publisher_groups.map(item => item.id),
        publisher_person_ids: draft.publisher_persons.map(item => item.id),
        vehicle_ids: draft.vehicles.map(item => item.id)
      });
      setSaved(listsOf(rule));
      setDraft(listsOf(rule));
      onSaved();
      return true;
    } catch (reason) {
      setError(errorMessage(reason, "Não foi possível salvar a regra do subtema."));
      return false;
    } finally {
      setSaving(false);
    }
  };
  // Sair com a regra alterada e não salva pergunta antes (`shared/unsaved-changes`).
  useUnsavedChanges(dirty, save, "a regra do subtema");

  const localPicker = (kind: Kind, all: AssignmentTarget[]) => {
    const chosen = new Set(draft[kind].map(item => item.id));
    return (
      <SearchSelect
        label={KIND_LABEL[kind]}
        value=""
        allLabel="Adicionar…"
        required
        options={all
          .filter(item => !chosen.has(item.id))
          .map(item => ({ value: String(item.id), label: item.name }))}
        onChange={value => {
          const target = all.find(item => String(item.id) === value);
          if (target) add(kind, target);
        }}
      />
    );
  };

  const chips = (kind: Kind) =>
    draft[kind].length > 0 && (
      <div className="tag-pill-list">
        {draft[kind].map(item => (
          <span key={item.id} className="tag-pill">
            {item.name}
            <button
              type="button"
              onClick={() => remove(kind, item.id)}
              aria-label={`Remover ${KIND_LABEL[kind].toLocaleLowerCase("pt-BR")} ${item.name}`}
            >
              ×
            </button>
          </span>
        ))}
      </div>
    );

  return (
    <div className="form-field mt-3 theme-assignment">
      <span>Atribuir este subtema automaticamente</span>
      <small>
        Toda publicação destes publicadores ou veículos recebe o subtema, sem passar
        pelos termos nem pela IA — e junto dos outros subtemas que o texto casar. Vale
        para o que for enviado a partir de agora; o que já entrou não muda.
      </small>
      {error && <div className="login-error">{error}</div>}
      {saved === null && !error && (
        <small><LoaderCircle className="spin" size={14} /> Carregando a regra…</small>
      )}
      {saved !== null && (
        <>
          <div className="theme-assignment-grid">
            <div>
              {localPicker("publisher_groups", publisherGroups)}
              {chips("publisher_groups")}
            </div>
            <div>
              {localPicker("publisher_persons", publisherPersons)}
              {chips("publisher_persons")}
            </div>
            <div>
              <SearchSelect
                label={KIND_LABEL.vehicles}
                value=""
                allLabel="Adicionar…"
                required
                options={[]}
                search={searchVehicles}
                onChange={value => {
                  const id = Number(value);
                  const name = vehicleNames.current.get(id);
                  if (name) add("vehicles", { id, name });
                }}
              />
              {chips("vehicles")}
            </div>
          </div>
          <div className="edit-form-actions">
            <button
              type="button"
              className="secondary-button mt-3"
              onClick={save}
              disabled={saving || !dirty}
            >
              {saving ? <LoaderCircle className="spin" size={16} /> : <Save size={16} />}
              Salvar regra
            </button>
            {dirty && <small className="mt-3">Há mudanças na regra ainda não salvas.</small>}
          </div>
        </>
      )}
    </div>
  );
}
